import get from 'lodash/get';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as PaymentsUtils from '../../utils';
import CancelLink from '../LegalLinks/CancelLink';
import SubscriptionTermsLink from '../LegalLinks/SubscriptionTermsLink';
import TrialOfferTermsLink from '../LegalLinks/TrialOfferTermsLink';

const log = D.Logger.child({ component: 'TOSAgreement/utils' });
const calculateTrialExpiryDate = PaymentsUtils.calculateTrialExpiryDate;
const getDisplayableBillingDate = PaymentsUtils.getDisplayableBillingDate;

export function getTOSAgreementMessages(offerData) {
  const {
    tier,
    discountType,
    trialPrice,
    offerTrialType,
    trialTimesRenewable,
    durationType,
    price,
    backingProductPrice,
    offerDurationTime,
    offerDurationType,
    zipCode,
    activeProductNextBillingDate,
    activeProductPrice,
    undiscountedPrice,
    intl,
    isInExperiment,
  } = offerData;
  const {
    planName,
    displayableTrialPrice,
    displayablePrice,
    displayableBackingProductPrice,
    frequencyForDurationType,
    displayableActiveProductPrice,
    displayableActiveProductNextBillingDate,
    displayableUndiscountedPrice,
  } = getDisplayValues({
    tier,
    discountType,
    durationType,
    price,
    backingProductPrice,
    trialPrice,
    intl,
    activeProductPrice,
    activeProductNextBillingDate,
    undiscountedPrice,
  });
  const isDC = isDCZipCode(zipCode);
  const isVT = isVTZipCode(zipCode);
  const isStudent = discountType === PaymentsConstants.discountTypes.STUDENT;
  const isMilitary = discountType === PaymentsConstants.discountTypes.MILITARY;
  const isFamily = tier === PaymentsConstants.tiers.PANDORA_FAMILY;
  const isFreeTrial = offerTrialType === PaymentsConstants.OfferTrialTypes.FREE;
  const hasIntroductoryPricing = !!trialPrice; // trial price exists and is not 0
  let agreementMessages = [];

  if (hasIntroductoryPricing) {
    const pricesAreSame = trialPrice === backingProductPrice;
    const displayablePricesAreSame =
      get(displayableTrialPrice, 'props.value') ===
      get(displayableBackingProductPrice, 'props.value');
    if (pricesAreSame || displayablePricesAreSame) {
      log.error('Intro pricing should not match backing product pricing.', offerData);
    }

    if (durationType === PaymentsConstants.DurationTypes.YEARLY) {
      agreementMessages = getIntroPricingYearlyMessages({
        intl,
        durationType,
        offerTrialType,
        activeProductNextBillingDate,
        isDC,
        isVT,
        isFreeTrial,
        displayableActiveProductPrice,
        displayableActiveProductNextBillingDate,
        planName,
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        displayablePrice,
        displayableBackingProductPrice,
        displayableTrialPrice,
        isInExperiment,
      });
    } else {
      // Monthly
      agreementMessages = getIntroPricingMonthlyMessages({
        intl,
        durationType,
        offerTrialType,
        activeProductNextBillingDate,
        isDC,
        isVT,
        isFreeTrial,
        displayableActiveProductPrice,
        displayableActiveProductNextBillingDate,
        planName,
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        displayablePrice,
        displayableBackingProductPrice,
        displayableTrialPrice,
        isInExperiment,
      });
    }
  } else {
    // Free trial or no trial
    if (isDC || (isVT && durationType === PaymentsConstants.DurationTypes.YEARLY)) {
      agreementMessages = getRegularPricingSpecialCaseMessages({
        intl,
        isStudent,
        isMilitary,
        discountType,
        isFreeTrial,
        isFamily,
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        displayablePrice,
        frequencyForDurationType,
        displayableUndiscountedPrice,
        isInExperiment,
      });
    } else {
      agreementMessages = getRegularPricingMessages({
        intl,
        isStudent,
        isMilitary,
        discountType,
        isFreeTrial,
        isFamily,
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        displayablePrice,
        frequencyForDurationType,
        displayableUndiscountedPrice,
        isInExperiment,
      });
    }
  }
  return agreementMessages;
}

export function getIntroPricingYearlyMessages({
  intl,
  durationType,
  offerTrialType,
  activeProductNextBillingDate,
  isDC,
  isVT,
  isFreeTrial,
  displayableActiveProductPrice,
  displayableActiveProductNextBillingDate,
  planName,
  offerDurationTime,
  offerDurationType,
  trialTimesRenewable,
  displayableBackingProductPrice,
  displayableTrialPrice,
  displayablePrice,
  isInExperiment,
}) {
  const agreementMessages = [];
  let sixMonthTrialExpirationDate;
  let sixMonthDisplayableBillingDate;
  let is6MPremiumTrialExpiringAfterActiveProduct;
  if (offerTrialType === PaymentsConstants.OfferTrialTypes.SIX_MONTH_ANNUAL_PREMIUM) {
    sixMonthTrialExpirationDate = calculateTrialExpiryDate(
      new Date(),
      6,
      PaymentsConstants.DurationTypes.MONTHLY,
      0,
    );
    sixMonthDisplayableBillingDate = getDisplayableBillingDate(
      6,
      PaymentsConstants.DurationTypes.MONTHLY,
      0,
      intl,
    );
    is6MPremiumTrialExpiringAfterActiveProduct =
      sixMonthTrialExpirationDate > new Date(activeProductNextBillingDate);
  }

  if (isDC || isVT || isInExperiment) {
    const annualIntroPricingBillingStartAgreementValues = {
      renewDuringTrialAgreement: is6MPremiumTrialExpiringAfterActiveProduct ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.renewDuringTrialAgreement}
          values={{
            priorTierPrice: displayableActiveProductPrice,
            currentRenewalDate: displayableActiveProductNextBillingDate,
          }}
        />
      ) : (
        ''
      ),
      offerTier: planName,
      dayAfterExpirationDate:
        sixMonthDisplayableBillingDate ||
        getDisplayableBillingDate(offerDurationTime, offerDurationType, trialTimesRenewable, intl),
      price: displayableBackingProductPrice,
    };
    const agreementMessageFirstPartValues = {
      subscriptionTerms: (
        <SubscriptionTermsLink
          discountType={PaymentsConstants.discountTypes.NONE}
          isBlueUrl={true}
        />
      ),
      offerTerms: (
        <FormattedMessage
          {...PaymentsConstants.Messages.andOfferTerms}
          values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
        />
      ),
      annualIntroPricingBillingStartAgreement: isInExperiment ? (
        ''
      ) : (
        <FormattedMessage
          {...PaymentsConstants.Messages.annualIntroPricingBillingStartAgreement}
          values={annualIntroPricingBillingStartAgreementValues}
        />
      ),
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.annualIntroPricingAgreementFirstPart,
      values: agreementMessageFirstPartValues,
    });
    const agreementMessageSecondPartValues = {
      cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
      annualIntroPricingBillingStartAgreementExperiment: isInExperiment ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.annualIntroPricingBillingStartAgreementExperiment}
          values={annualIntroPricingBillingStartAgreementValues}
        />
      ) : (
        ''
      ),
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.annualIntroPricingAgreementSecondPart,
      values: agreementMessageSecondPartValues,
    });
  } else {
    const agreementMessageValues = {
      subscriptionTerms: (
        <SubscriptionTermsLink
          discountType={PaymentsConstants.discountTypes.NONE}
          isBlueUrl={true}
        />
      ),
      offerTerms: (
        <FormattedMessage
          {...PaymentsConstants.Messages.andOfferTerms}
          values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
        />
      ),
      renewDuringTrialAgreement: is6MPremiumTrialExpiringAfterActiveProduct ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.renewDuringTrialAgreement}
          values={{
            priorTierPrice: displayableActiveProductPrice,
            currentRenewalDate: displayableActiveProductNextBillingDate,
          }}
        />
      ) : (
        ''
      ),
      offerTier: planName,
      dayAfterExpirationDate:
        sixMonthDisplayableBillingDate ||
        getDisplayableBillingDate(offerDurationTime, offerDurationType, trialTimesRenewable, intl),
      price: displayableBackingProductPrice,
      cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.annualIntroPricingAgreementCombined,
      values: agreementMessageValues,
    });
  }
  return agreementMessages;
}

export function getIntroPricingMonthlyMessages({
  intl,
  durationType,
  offerTrialType,
  activeProductNextBillingDate,
  isDC,
  isVT,
  isFreeTrial,
  displayableActiveProductPrice,
  displayableActiveProductNextBillingDate,
  planName,
  offerDurationTime,
  offerDurationType,
  trialTimesRenewable,
  displayablePrice,
  displayableBackingProductPrice,
  displayableTrialPrice,
  isInExperiment,
}) {
  const agreementMessages = [];
  if (isDC || isInExperiment) {
    const monthlyIntroPricingBillingStartAgreementValues = {
      offerTier: planName,
      trialPrice: displayableTrialPrice,
      numMonths: trialTimesRenewable + 1,
      price: displayableBackingProductPrice,
      dayAfterExpirationDate: getDisplayableBillingDate(
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        intl,
      ),
    };
    const agreementMessageFirstPartValues = {
      subscriptionTerms: (
        <SubscriptionTermsLink
          discountType={PaymentsConstants.discountTypes.NONE}
          isBlueUrl={true}
        />
      ),
      offerTerms: (
        <FormattedMessage
          {...PaymentsConstants.Messages.andOfferTerms}
          values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
        />
      ),
      monthlyIntroPricingBillingStartAgreement: isInExperiment ? (
        ''
      ) : (
        <FormattedMessage
          {...PaymentsConstants.Messages.monthlyIntroPricingBillingStartAgreement}
          values={monthlyIntroPricingBillingStartAgreementValues}
        />
      ),
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.monthlyIntroPricingAgreementFirstPart,
      values: agreementMessageFirstPartValues,
    });
    const agreementMessageSecondPartValues = {
      cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
      monthlyIntroPricingBillingStartAgreementExperiment: isInExperiment ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.monthlyIntroPricingBillingStartAgreementExperiment}
          values={monthlyIntroPricingBillingStartAgreementValues}
        />
      ) : (
        ''
      ),
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.monthlyIntroPricingAgreementSecondPart,
      values: agreementMessageSecondPartValues,
    });
  } else {
    const agreementMessageValues = {
      subscriptionTerms: (
        <SubscriptionTermsLink
          discountType={PaymentsConstants.discountTypes.NONE}
          isBlueUrl={true}
        />
      ),
      offerTerms: (
        <FormattedMessage
          {...PaymentsConstants.Messages.andOfferTerms}
          values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
        />
      ),
      offerTier: planName,
      trialPrice: displayableTrialPrice,
      numMonths: trialTimesRenewable + 1,
      price: displayableBackingProductPrice,
      dayAfterExpirationDate: getDisplayableBillingDate(
        offerDurationTime,
        offerDurationType,
        trialTimesRenewable,
        intl,
      ),
      cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.monthlyIntroPricingAgreementCombined,
      values: agreementMessageValues,
    });
  }
  return agreementMessages;
}

function getRegularPricingSpecialCaseMessages({
  intl,
  isStudent,
  isMilitary,
  discountType,
  isFreeTrial,
  isFamily,
  offerDurationTime,
  offerDurationType,
  trialTimesRenewable,
  displayablePrice,
  frequencyForDurationType,
  displayableUndiscountedPrice,
  isInExperiment,
}) {
  const agreementMessages = [];
  const trialAgreementValues = {
    billingStartDate: getDisplayableBillingDate(
      offerDurationTime,
      offerDurationType,
      trialTimesRenewable,
      intl,
    ),
    price: displayablePrice,
    frequency: frequencyForDurationType,
  };
  const agreementMessageFirstPartValues = {
    extraSubscriptionTerms:
      isStudent || isMilitary ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.extraSubscriptionTerms}
          values={{
            terms: <SubscriptionTermsLink discountType={discountType} isBlueUrl={true} />,
          }}
        />
      ) : (
        ''
      ),
    baseSubscriptionTerms: (
      <SubscriptionTermsLink discountType={PaymentsConstants.discountTypes.NONE} isBlueUrl={true} />
    ),
    offerTerms: isFreeTrial ? (
      <FormattedMessage
        {...PaymentsConstants.Messages.andOfferTerms}
        values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
      />
    ) : (
      ''
    ),
    familyAgreement: isFamily ? (
      <FormattedMessage {...PaymentsConstants.Messages.familyAgreement} />
    ) : (
      ''
    ),
    trialAgreement:
      isFreeTrial && !isInExperiment ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.trialAgreement}
          values={trialAgreementValues}
        />
      ) : (
        ''
      ),
  };
  agreementMessages.push({
    message: PaymentsConstants.Messages.agreementFirstPart,
    values: agreementMessageFirstPartValues,
  });
  const agreementMessageSecondPartValues = {
    trialAgreementExperiment:
      isFreeTrial && isInExperiment ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.trialAgreementExperiment}
          values={trialAgreementValues}
        />
      ) : (
        ''
      ),
    cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
    frequency: frequencyForDurationType,
    offerPrice: displayablePrice,
    studentAgreement: isStudent ? (
      <FormattedMessage
        {...PaymentsConstants.Messages.studentAgreement}
        values={{
          price: displayableUndiscountedPrice,
          frequency: frequencyForDurationType,
        }}
      />
    ) : (
      ''
    ),
  };
  agreementMessages.push({
    message: PaymentsConstants.Messages.agreementSecondPart,
    values: agreementMessageSecondPartValues,
  });
  return agreementMessages;
}

function getRegularPricingMessages({
  intl,
  isStudent,
  isMilitary,
  discountType,
  isFreeTrial,
  isFamily,
  offerDurationTime,
  offerDurationType,
  trialTimesRenewable,
  displayablePrice,
  frequencyForDurationType,
  displayableUndiscountedPrice,
  isInExperiment,
}) {
  if (isInExperiment) {
    const agreementMessages = [];
    const agreementMessageFirstPartValues = {
      extraSubscriptionTerms:
        isStudent || isMilitary ? (
          <FormattedMessage
            {...PaymentsConstants.Messages.extraSubscriptionTerms}
            values={{
              terms: <SubscriptionTermsLink discountType={discountType} isBlueUrl={true} />,
            }}
          />
        ) : (
          ''
        ),
      baseSubscriptionTerms: (
        <SubscriptionTermsLink
          discountType={PaymentsConstants.discountTypes.NONE}
          isBlueUrl={true}
        />
      ),
      offerTerms: isFreeTrial ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.andOfferTerms}
          values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
        />
      ) : (
        ''
      ),
      familyAgreement: isFamily ? (
        <FormattedMessage {...PaymentsConstants.Messages.familyAgreement} />
      ) : (
        ''
      ),
      trialAgreement: '',
    };
    const agreementMessageSecondPartValues = {
      cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
      frequency: frequencyForDurationType,
      offerPrice: displayablePrice,
      studentAgreement: isStudent ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.studentAgreement}
          values={{
            price: displayableUndiscountedPrice,
            frequency: frequencyForDurationType,
          }}
        />
      ) : (
        ''
      ),
      trialAgreementExperiment: isFreeTrial ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.trialAgreementExperiment}
          values={{
            billingStartDate: getDisplayableBillingDate(
              offerDurationTime,
              offerDurationType,
              trialTimesRenewable,
              intl,
            ),
            price: displayablePrice,
            frequency: frequencyForDurationType,
          }}
        />
      ) : (
        ''
      ),
    };
    agreementMessages.push({
      message: PaymentsConstants.Messages.agreementFirstPart,
      values: agreementMessageFirstPartValues,
    });
    agreementMessages.push({
      message: PaymentsConstants.Messages.agreementSecondPart,
      values: agreementMessageSecondPartValues,
    });
    return agreementMessages;
  }
  const agreementMessages = [];
  const agreementMessageValues = {
    extraSubscriptionTerms:
      isStudent || isMilitary ? (
        <FormattedMessage
          {...PaymentsConstants.Messages.extraSubscriptionTerms}
          values={{
            terms: <SubscriptionTermsLink discountType={discountType} isBlueUrl={true} />,
          }}
        />
      ) : (
        ''
      ),
    baseSubscriptionTerms: (
      <SubscriptionTermsLink discountType={PaymentsConstants.discountTypes.NONE} isBlueUrl={true} />
    ),
    offerTerms: isFreeTrial ? (
      <FormattedMessage
        {...PaymentsConstants.Messages.andOfferTerms}
        values={{ offerTermsLink: <TrialOfferTermsLink isBlueUrl={true} /> }}
      />
    ) : (
      ''
    ),
    familyAgreement: isFamily ? (
      <FormattedMessage {...PaymentsConstants.Messages.familyAgreement} />
    ) : (
      ''
    ),
    trialAgreement: isFreeTrial ? (
      <FormattedMessage
        {...PaymentsConstants.Messages.trialAgreement}
        values={{
          billingStartDate: getDisplayableBillingDate(
            offerDurationTime,
            offerDurationType,
            trialTimesRenewable,
            intl,
          ),
          price: displayablePrice,
          frequency: frequencyForDurationType,
        }}
      />
    ) : (
      ''
    ),
    cancelInstructions: <CancelLink isBlueUrl={true} useInstructionsMessage={true} />,
    frequency: frequencyForDurationType,
    offerPrice: displayablePrice,
    studentAgreement: isStudent ? (
      <FormattedMessage
        {...PaymentsConstants.Messages.studentAgreement}
        values={{
          price: displayableUndiscountedPrice,
          frequency: frequencyForDurationType,
        }}
      />
    ) : (
      ''
    ),
  };
  agreementMessages.push({
    message: PaymentsConstants.Messages.agreementCombined,
    values: agreementMessageValues,
  });
  return agreementMessages;
}

export function isVTZipCode(zipCode) {
  // i18n - needs more granular test when we expand outside U.S.
  return (zipCode >= '05001' && zipCode <= '05495') || (zipCode >= '05601' && zipCode <= '05907');
}

export function isDCZipCode(zipCode) {
  // i18n - needs more granular test when we expand outside U.S.
  return (
    (zipCode >= '20001' && zipCode <= '20039') ||
    (zipCode >= '20042' && zipCode <= '20599') ||
    zipCode === '20799'
  );
}

export function getDisplayValues({
  tier,
  discountType,
  durationType,
  price,
  backingProductPrice,
  trialPrice,
  intl,
  activeProductPrice,
  activeProductNextBillingDate,
  undiscountedPrice,
}) {
  const planName = PaymentsUtils.getPlanName(tier, discountType);
  const displayableTrialPrice = D.Utils.StringUtils.returnPriceString(trialPrice, intl.locale);
  const displayablePrice = D.Utils.StringUtils.returnPriceString(price, intl.locale);
  const displayableBackingProductPrice = D.Utils.StringUtils.returnPriceString(
    backingProductPrice,
    intl.locale,
  );
  const frequencyForDurationType = PaymentsUtils.getPaymentFrequencyForDurationType(durationType);
  const displayableActiveProductNextBillingDate =
    activeProductNextBillingDate && PaymentsUtils.formatDate(intl, activeProductNextBillingDate);
  const displayableActiveProductPrice = D.Utils.StringUtils.returnPriceString(
    activeProductPrice,
    intl.locale,
  );
  const displayableUndiscountedPrice = D.Utils.StringUtils.returnPriceString(
    undiscountedPrice,
    intl.locale,
  );

  return {
    planName,
    displayableTrialPrice,
    displayablePrice,
    displayableBackingProductPrice,
    frequencyForDurationType,
    displayableActiveProductPrice,
    displayableActiveProductNextBillingDate,
    displayableUndiscountedPrice,
  };
}
